import React, { FunctionComponent } from 'react';
import {
  ButtonBase,
  Chip,
  ChipProps,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../../components/V2/Table/type1/TableStyles';
import {
  IMeetingRequest,
  MEETING_REQUEST_REQUESTED_BY_ROLES,
  MEETING_REQUEST_TYPES,
} from '../../../../../../../store/V2/MeetingRequestsSlice';
import { IUser } from '../../../../../../../store/UserSlice';
import { MEETING_SECTION_PAYMENT_TYPES } from '../../../../../../../store/V2/SectionSlice';

interface NewRequestTableRowProps {
  requestData: IMeetingRequest;
  selected: boolean;
  onSelectRow: () => void;
  onView: () => void;
}

const actionButtonStyles: SxProps = {
  borderRadius: '8px',
  padding: '10px 12px 10px 12px',
  fontWeight: 600,
  fontSize: '14px',
  maxHeight: '36px',
  backgroundColor: '#006C9C',
  color: '#F8FAFC',
};

const NewRequestTableRow: FunctionComponent<NewRequestTableRowProps> = ({
  requestData,
  onView,
}) => {
  const reason = requestData.reason || '-';
  const requestedAt = moment(requestData.createdAt);
  const scheduledAt = requestData.start ? moment(requestData.start) : undefined;
  const newScheduledAt = requestData.newStart
    ? moment(requestData.newStart)
    : null;

  const rescheduleTypeBadgeProps: ChipProps = {
    size: 'small',
    label: requestData.type.toLocaleLowerCase(),
    color: 'warning',
    sx: {
      textTransform: 'capitalize',
      width: '100%',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 600,
    },
  };

  let tutorName = '-';
  let studentName = '-';
  let tutorPenalty = '-';
  let studentPenalty = '-';

  if (
    requestData.penalty &&
    requestData.requestedByRole !== MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT
  ) {
    tutorPenalty = `${parseFloat(requestData.penalty.toFixed(2))}%`;
  }

  if (
    requestData.penalty &&
    requestData.requestedByRole === MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT
  ) {
    studentPenalty = `${parseFloat(requestData.penalty.toFixed(2))}%`;
  }

  if (requestData.tutor) {
    const _tutor = requestData.tutor as IUser;

    tutorName = _tutor.firstname;

    if (_tutor.lastname) {
      tutorName += ` ${_tutor.lastname}`;
    }
  }

  if (requestData.student) {
    const _student = requestData.student as IUser;

    studentName = _student.firstname;

    if (_student.lastname) {
      studentName += ` ${_student.lastname}`;
    }
  }

  switch (requestData.type) {
    case MEETING_REQUEST_TYPES.CANCEL:
      rescheduleTypeBadgeProps.color = 'error';
      break;

    case MEETING_REQUEST_TYPES.DELETE:
      rescheduleTypeBadgeProps.color = 'error';
      break;

    default:
      break;
  }

  return (
    <StyledTableRow hover key={requestData._id}>
      {/* <StyledTableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </StyledTableCell> */}
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {tutorName}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {studentName}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {requestedAt.format('ddd MMM DD YYYY')}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {requestedAt.format('hh:mm A')}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {scheduledAt ? scheduledAt.format('ddd MMM DD YYYY') : '-'}
        </Typography>
        {scheduledAt ? (
          <Typography fontSize="12px" color="#94A3B8">
            {scheduledAt.format('hh:mm A')}
          </Typography>
        ) : null}
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {newScheduledAt ? newScheduledAt.format('ddd MMM DD YYYY') : '-'}
        </Typography>
        {newScheduledAt ? (
          <Typography fontSize="12px" color="#94A3B8">
            {newScheduledAt.format('hh:mm A')}
          </Typography>
        ) : null}
      </StyledTableCell>
      <StyledTableCell>
        <Chip {...rescheduleTypeBadgeProps} />
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {requestData.requestedByRole
            ? requestData.requestedByRole.toLowerCase()
            : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {requestData.lessonType ? requestData.lessonType.toLowerCase() : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        {requestData.paymentType ? (
          <Typography
            fontSize="14px"
            fontWeight={500}
            textTransform="capitalize"
            color={
              requestData.paymentType === MEETING_SECTION_PAYMENT_TYPES.BILLABLE
                ? 'success.main'
                : 'warning.main'
            }
          >
            {requestData.paymentType.toLowerCase()}
          </Typography>
        ) : (
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            textTransform="capitalize"
          >
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {tutorPenalty}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {studentPenalty}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {reason}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <ButtonBase sx={{ ...actionButtonStyles }} onClick={onView}>
          View
        </ButtonBase>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default NewRequestTableRow;
